import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { EventView } from '@goaltree/backend/convex/events'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EventListRow } from '@/components/event-row'
import { InboxTaskDetailSheet, toSheetTask } from '@/components/inbox-task-detail-sheet'
import { useUpcomingWindow } from '@/components/today-schedule'
import { TodaySectionHeader } from '@/components/today-section-header'
import { useNow } from '@/hooks/use-now'
import { addLocalDays } from '@/lib/events'
import { completeNode, uncompleteNode } from '@/lib/nodes-optimistic'

const LIMIT = 5

type DueTask = { _id: Id<'nodes'>; title: string; dueDate: number; completedAt: number | null }
type Item =
  | { kind: 'event'; at: number; event: EventView }
  | { kind: 'task'; at: number; task: DueTask }

function dayShort(ms: number, now: number, locale: string) {
  const within = ms - now < 6 * 86_400_000
  return new Date(ms).toLocaleDateString(
    locale,
    within ? { weekday: 'short' } : { month: 'short', day: 'numeric' },
  )
}

function TaskRow({
  task,
  now,
  onOpen,
  onToggle,
}: {
  task: DueTask
  now: number
  onOpen: () => void
  onToggle: () => void
}) {
  const { t, i18n } = useTranslation()
  const done = task.completedAt != null
  return (
    <div className="flex min-h-12 min-w-0 items-center gap-2.5 rounded-lg px-2 py-2">
      <span aria-hidden className="w-9 shrink-0" />
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={t('todaySchedule.markDone', { title: task.title })}
        onClick={onToggle}
        className={cn(
          'flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-[5px] border-[1.5px] transition-colors',
          done
            ? 'border-primary bg-primary text-white dark:text-[#0a0a0a]'
            : 'border-muted-foreground/50 hover:border-foreground',
        )}
      >
        {done && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
      </button>
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('upcoming.openTask', { title: task.title })}
        className={cn(
          'min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:underline hover:underline-offset-2',
          done && 'text-muted-foreground line-through decoration-muted-foreground/50',
        )}
      >
        {task.title}
      </button>
      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-mono text-[10.5px] text-muted-foreground">
        {t('todaySchedule.due', { day: dayShort(task.dueDate, now, i18n.language) })}
      </span>
    </div>
  )
}

export function ComingUpPanel({
  className,
  limit = LIMIT,
  hideWhenEmpty,
}: {
  className?: string
  limit?: number
  hideWhenEmpty?: boolean
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const now = useNow(60_000)
  const { from, data } = useUpcomingWindow()
  const tomorrow = addLocalDays(from, 1)
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((localStore, { taskId }) =>
    completeNode(localStore, taskId),
  )
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) => uncompleteNode(localStore, taskId),
  )
  const [openTaskId, setOpenTaskId] = useState<Id<'nodes'> | null>(null)
  // Mounted on first open only, so a remembered sheet elsewhere on Today
  // isn't restored here too.
  const [sheetMounted, setSheetMounted] = useState(false)
  const openDoc = useQuery(api.events.taskForSheet, openTaskId ? { taskId: openTaskId } : 'skip')
  const openTask = useMemo(() => (openDoc ? toSheetTask(openDoc) : null), [openDoc])

  const items: Item[] = data
    ? [
        ...data.events
          .filter((e) => e.startsAt >= tomorrow)
          .map((e) => ({ kind: 'event' as const, at: e.startsAt, event: e })),
        ...data.dueTasks
          .filter((d) => d.dueDate >= tomorrow)
          .map((d) => ({ kind: 'task' as const, at: d.dueDate, task: d })),
      ]
        .sort((a, b) => a.at - b.at)
        .slice(0, limit)
    : []

  if (hideWhenEmpty && !items.length) return null

  return (
    <aside
      className={cn('w-80 shrink-0 xl:w-96', className)}
      aria-label={t('todaySchedule.comingUp')}
    >
      <TodaySectionHeader
        label={t('todaySchedule.comingUp')}
        trailing={
          <button
            type="button"
            onClick={() => navigate({ to: '/everyday', search: { tab: 'events' } })}
            className="cursor-pointer text-[11px] font-semibold normal-case tracking-normal text-primary-text hover:underline"
          >
            {t('todaySchedule.seeAll')}
          </button>
        }
        className="mt-0"
      />
      {data && !items.length && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('todaySchedule.nothingSoon')}
        </p>
      )}
      <div className="-mx-2 flex flex-col">
        {items.map((it) =>
          it.kind === 'event' ? (
            <EventListRow
              key={it.event._id}
              event={it.event}
              now={now}
              dayPrefix={dayShort(it.at, now, i18n.language)}
            />
          ) : (
            <TaskRow
              key={it.task._id}
              task={it.task}
              now={now}
              onOpen={() => {
                setSheetMounted(true)
                setOpenTaskId(it.task._id)
              }}
              onToggle={() =>
                void (it.task.completedAt != null
                  ? uncomplete({ taskId: it.task._id })
                  : complete({ taskId: it.task._id }))
              }
            />
          ),
        )}
      </div>
      {sheetMounted && (
        <InboxTaskDetailSheet
          task={openTask}
          onClose={() => setOpenTaskId(null)}
          onComplete={(taskId) => void complete({ taskId })}
          onUncomplete={(taskId) => void uncomplete({ taskId })}
        />
      )}
    </aside>
  )
}
